"use client";

import { FileText, LoaderCircle } from "lucide-react";
import type { PdfOptions } from "@/src/lib/pdf/print-document";
import {
  fontLabels,
  marginLabels,
  orientationLabels,
  paperSizeLabels,
  themeLabels,
  codeThemeLabels,
  diagramThemeLabels,
} from "./labels";

export function formatPreviewOptions(options: PdfOptions) {
  return [
    paperSizeLabels[options.paperSize],
    orientationLabels[options.orientation],
    marginLabels[options.margins],
    themeLabels[options.theme],
    codeThemeLabels[options.codeTheme],
    `diagram ${diagramThemeLabels[options.diagramTheme]}`,
    fontLabels[options.fontFamily],
    `${options.fontSize}pt`,
    `code block ${fontLabels[options.codeFontFamily]} ${options.codeFontSize}pt`,
    `diagram ${fontLabels[options.diagramFontFamily]} ${options.diagramFontSize}pt`,
  ].join(" / ");
}

export function EmptyPreview() {
  return (
    <div className="flex max-w-60 flex-col items-center text-center">
      <FileText aria-hidden="true" className="size-9 text-zinc-300" />
      <p className="mt-3 text-sm font-semibold text-zinc-700">
        Nothing to preview
      </p>
      <p className="mt-1 text-xs leading-5 text-zinc-500">
        Add markdown in the editor to render a document preview.
      </p>
    </div>
  );
}

export function RenderingPreview({
  pdfOptions,
}: Readonly<{ pdfOptions: PdfOptions }>) {
  return (
    <div className="flex max-w-60 flex-col items-center text-center">
      <LoaderCircle
        aria-hidden="true"
        className="size-9 animate-spin text-brand"
      />
      <p className="mt-3 text-sm font-semibold text-zinc-700">
        Rendering preview
      </p>
      <p className="mt-1 text-xs leading-5 text-zinc-500">
        {formatPreviewOptions(pdfOptions)}
      </p>
    </div>
  );
}

export function PreviewError({ message }: Readonly<{ message: string }>) {
  return (
    <div
      role="alert"
      className="max-w-sm rounded-md border border-red-200 bg-red-50 px-4 py-3 text-left"
    >
      <p className="text-sm font-semibold text-red-800">
        Preview could not render
      </p>
      <p className="mt-1 text-xs leading-5 text-red-700">{message}</p>
    </div>
  );
}
